import {
	IRegulatedMarketRequirements,
	UKRegulatedMarketAutoplaySettings,
	UnregulatedMarketAutoplaySettings,
	IAutoplaySettings,
} from '@fantasmagames/slot/src/modules/autoplay/AutoplayHandlerModule';
import {Views} from '@fantasmagames/slot/src/common/view/Views';
import {Module} from '@fantasmagames/slot/src/common/Module';
import {AutoPlayButtonEvents} from '@fantasmagames/slot/src/modules/keypad/autoplay/AutoPlayButtonEvents';
import {AutoplayMenuViewEvents} from '@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/AutoplayMenuViewEvents';
import {LogicEvents} from '@fantasmagames/slot/src/logic/LogicEvents';
import {AutoplayMenuView} from '@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/AutoplayMenuView';
import {AutoplayMenuBasicState} from '@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/states/AutoplayMenuBasicState';
import {AutoplayMenuOpenState} from '@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/states/AutoplayMenuOpenState';
import {AutoplayMenuEvents} from '@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/AutoplayMenuEvents';
import {ReelsEvents} from '@fantasmagames/slot/src/modules/reels/spin/ReelsEvents';
import {QueueEventsHandler} from '@fantasmagames/slot/src/modules/flow/states/QueueEventsHandler';

export class AutoplayMenuModule extends Module<AutoplayMenuView> {
	public static readonly BASIC: string = 'BASIC';
	public static readonly OPEN: string = 'OPEN';

	protected _requiredSettings: IRegulatedMarketRequirements = UnregulatedMarketAutoplaySettings;
	protected _regulatedMarket: string;
	protected _queueEvents: QueueEventsHandler;

	protected _winLimit: number;
	protected _lossLimit: number;
	protected _singleWinLimit: number;
	protected _selectedSpins: number;

	public init(): void {
		super.init();

		this._queueEvents = new QueueEventsHandler(this.eventManager);
		this.setupRegulatedMarket();
		this.setupMarketSettings();
	}

	protected setupRegulatedMarket(): void {
		this._regulatedMarket = 'none';
	}

	protected isSingleWinLimitAvailable(): boolean {
		return true;
	}

	protected isWinLimitAvailable(): boolean {
		return true;
	}

	protected isLossLimitAvailable(): boolean {
		return true;
	}

	protected setupMarketSettings(): void {
		switch (this._regulatedMarket) {
			case 'uk':
				this._requiredSettings = UKRegulatedMarketAutoplaySettings;
				break;
			case 'sv':
				break;
			case 'it':
				break;
			case 'de':
				break;
			case 'da':
				break;
			default:
				break;
		}
	}

	protected setupStates(): void {
		this.addState(new AutoplayMenuBasicState(this, AutoplayMenuModule.BASIC));
		this.addState(new AutoplayMenuOpenState(this, AutoplayMenuModule.OPEN));
	}

	protected enterFirstState(): void {
		this.changeStateTo(AutoplayMenuModule.BASIC);
	}

	protected setupViews(): void {
		if (this.system.shouldUseMultipleViews()) {
			this.setView(Views.AUTOPLAY_MENU_VIEW_MOBILE);
		} else {
			this.setView(Views.AUTOPLAY_MENU_VIEW);
		}
	}

	protected setupEvents(): void {
		super.setupEvents();

		this.on(AutoPlayButtonEvents.AUTO_PLAYBUTTON_CLICKED, this.onAutoPlayButtonClicked);
		this.on(LogicEvents.INIT_RESPONSE, this.onInitResponse);
		this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onSpinStarted);

		this.onView(AutoplayMenuViewEvents.ACCEPTED, this.onAccepted);
		this.onView(AutoplayMenuViewEvents.CANCELLED, this.onCancelled);

		this.onView(AutoplayMenuView.WIN_LIMIT_CHANGED, this.onWinLimitChanged);
		this.onView(AutoplayMenuView.SINGLE_WIN_LIMIT_CHANGED, this.onSingleWinLimitChanged);
		this.onView(AutoplayMenuView.LOSS_LIMIT_CHANGED, this.onLimitLossChanged);
		this.onView(AutoplayMenuView.SPINS_AMOUNT_CHANGED, this.onSpinsChanged);

		this.onViewEmitEvent(AutoplayMenuViewEvents.AUTO_PLAY_MENU_CLOSED, AutoplayMenuEvents.AUTO_PLAY_MENU_CLOSED);
		this.onViewEmitEvent(AutoplayMenuViewEvents.AUTO_PLAY_MENU_OPENED, AutoplayMenuEvents.AUTO_PLAY_MENU_OPENED);
		this.onViewEmitEvent(AutoplayMenuViewEvents.UPDATE_AUTOPLAY_VALUES, AutoplayMenuEvents.UPDATE_AUTOPLAY_VALUES);
	}

	protected onWinLimitChanged(eventID: string, newLimit: number): void {
		this.winLimit = newLimit;
		this.validateRequirements();
	}

	protected onSingleWinLimitChanged(eventID: string, newLimit: number): void {
		this.singleWinLimit = newLimit;
		this.validateRequirements();
	}

	protected onLimitLossChanged(eventID: string, newLimit: number): void {
		this.lossLimit = newLimit;
		this.validateRequirements();
	}

	protected onSpinsChanged(eventID: string, newSpins: number): void {
		this.selectedSpins = newSpins;
		this.validateRequirements();
	}

	protected validateRequirements(): void {
		let hasValidSettings: boolean = true;

		if (hasValidSettings && this.requiredSettings.lossLimitRequired) {
			hasValidSettings = hasValidSettings && this.hasValidLossLimit();
			if (!hasValidSettings) {
				this.view.disableMenuDueToLossLimitExceedsBet();
			}
		}

		if (hasValidSettings && this.isLossLimitAvailable()) {
			hasValidSettings = hasValidSettings && (this.isLossLimitDisabled() || this.hasValidLossLimit());
			if (!hasValidSettings) {
				this.view.disableMenuDueToLossLimitExceedsBet();
			}
		}

		if (hasValidSettings && this.requiredSettings.winLimitRequired) {
			hasValidSettings = hasValidSettings && this.hasValidWinLimit();
			if (!hasValidSettings) {
				this.view.disableMenuDueToWinLimitTooLow();
			}
		}

		if (hasValidSettings && this.requiredSettings.singleWinLimitRequired) {
			hasValidSettings = hasValidSettings && this.hasValidSingleWinLimit();
			if (!hasValidSettings) {
				this.view.disableMenuDueToSingleWinLimitTooLow();
			}
		}

		if (hasValidSettings && this.requiredSettings.maxSpinsRequired) {
			hasValidSettings = hasValidSettings && this.hasValidMaxSpins();
			if (!hasValidSettings) {
				this.view.disableMenuDueToSpinAmountTooHigh();
			}
		}

		if (hasValidSettings) {
			this.enableAcceptLimitsOK();
		}
	}

	protected enableAcceptLimitsOK(): void {
		this.view.enableMenuLimitsOK();
	}

	protected hasValidLossLimit(): boolean {
		return this.lossLimit >= this.logic.betValue;
	}

	protected isLossLimitDisabled(): boolean {
		return this.lossLimit === 0;
	}

	protected hasValidMaxSpins(): boolean {
		return this.view.autoplayAmount <= this.requiredSettings.maxSpins;
	}

	protected hasValidSingleWinLimit(): boolean {
		return this.singleWinLimit > 0;
	}

	protected hasValidWinLimit(): boolean {
		return this.winLimit > 0;
	}

	protected onSpinStarted(): void {
		this.view.hide();
	}

	protected onAccepted(eventID: string, autoplayRounds: number): void {
		this.logic.lastAutoplayAmount = autoplayRounds;
		this.saveData();
		this.startAutoPlay(eventID, autoplayRounds);
		this.gotoState(AutoplayMenuModule.BASIC);
	}

	protected saveData(): void {
		if (this.canSaveData) {
			this.logic.saveData();
		}
	}

	protected get canSaveData(): boolean {
		return true;
	}

	protected onCancelled(): void {
		this.gotoState(AutoplayMenuModule.BASIC);
		this.eventManager.emit(AutoplayMenuEvents.CANCELLED);
	}

	protected onAutoPlayButtonClicked(): void {
		this.showMenu();
	}

	protected showMenu(): void {
		this.gotoState(AutoplayMenuModule.OPEN);
		this.view.setSliderValues(this._logic.autoplayData, this.logic.betValue);
		this.view.resetSliders();
		this.validateRequirements();
	}

	protected onInitResponse(): void {
		if (this.logic.autoplayData) {
			this.view.buttonValues = this.logic.autoplayData.spinCount;
		} else {
			this.view.buttonValues = this.logic.autoplayValues;
		}

		if (this.logic.lastAutoplayAmount) {
			this.view.autoplayAmount = this.logic.lastAutoplayAmount;
			this.view.lastAutoplayAmount = this.logic.lastAutoplayAmount;
		} else {
			this.view.autoplayAmount = this.logic.autoplayDefault;
			this.view.lastAutoplayAmount = this.logic.autoplayDefault;
		}
	}

	protected startAutoPlay(eventID: string, autoplayRounds: number): void {
		this.eventManager.emit(AutoplayMenuEvents.UPDATE_AUTOPLAY_VALUES, this.view.getCurrentAutoplayParameters());
		this.eventManager.emit(AutoplayMenuEvents.ACCEPTED, autoplayRounds);
	}

	public get requiredSettings(): IRegulatedMarketRequirements {
		return this._requiredSettings;
	}

	public get regulatedMarket(): string {
		return this._regulatedMarket;
	}

	public set winLimit(value: number) {
		this._winLimit = value;
	}

	public get winLimit(): number {
		return this._winLimit;
	}

	public set lossLimit(value: number) {
		this._lossLimit = value;
	}

	public get lossLimit(): number {
		return this._lossLimit;
	}

	public set singleWinLimit(value: number) {
		this._singleWinLimit = value;
	}

	public get singleWinLimit(): number {
		return this._singleWinLimit;
	}

	public set selectedSpins(value: number) {
		this._selectedSpins = value;
	}

	public get selectedSpins(): number {
		return this._selectedSpins;
	}

	public get freeRoundsAutoplayOptions(): IAutoplaySettings {
		return {
			enabled: this.logic.autoplayData.enabled,
			spinCount: this.logic.autoplayData.spinCount,
			spinDefault: this.logic.autoplayData.spinDefault,
			untilStop: false,
			maxSpinsUntilStop: NaN,
			winLimitEnabled: false,
			winLimitsAmounts: [],
			winLimitDefault: NaN,
			lossLimitEnabled: false,
			lossLimitDefault: NaN,
			lossLimitsAmounts: [],
		};
	}

}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/AutoplayMenuModule.ts